import { Link } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout';
import AdBanner from '../components/AdBanner';
import { AD_SLOTS } from '../config/adSlots';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useStructuredData } from '../hooks/useStructuredData';

export default function ContactPage() {
  useDocumentMeta(
    'Contact',
    'Contact the Takedown League team for help with your account, leagues, or any questions about the platform.'
  );

  useStructuredData({
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact Takedown League',
    description: 'Get in touch with the Takedown League support team.',
    url: 'https://takedownleague.com/contact',
  });

  return (
    <PublicLayout>
      {/* Hero */}
      <div className="bg-gradient-to-br from-blue-600 via-purple-600 to-purple-700 text-white py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl font-bold mb-4">Contact Us</h1>
          <p className="text-xl text-purple-100">We are here to help.</p>
        </div>
      </div>

      <section className="bg-white py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Email */}
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <svg
                  className="w-6 h-6 text-purple-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h2 className="text-lg font-bold text-gray-900 mb-2">Email Support</h2>
              <p className="text-gray-600 mb-4 text-sm">
                Send us an email and we will get back to you as soon as possible.
              </p>
              <a
                href="mailto:support@takedownleague.com"
                className="text-purple-600 font-semibold hover:text-purple-800 transition-colors"
              >
                support@takedownleague.com
              </a>
            </div>

            {/* Live Chat */}
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <svg
                  className="w-6 h-6 text-purple-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </div>
              <h2 className="text-lg font-bold text-gray-900 mb-2">Live Chat</h2>
              <p className="text-gray-600 mb-4 text-sm">
                Chat with us in real time. The chat widget is available at the bottom right of every
                page when you are signed in.
              </p>
              <a
                href="https://takedownleague.tawk.help/category/getting-started"
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-600 font-semibold hover:text-purple-800 transition-colors"
              >
                Open Help Center →
              </a>
            </div>
          </div>

          <div className="mt-10">
            <AdBanner slot={AD_SLOTS.contact} format="horizontal" />
          </div>

          {/* FAQ callout */}
          <div className="mt-10 bg-purple-50 border border-purple-200 rounded-xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-1">Looking for quick answers?</h3>
              <p className="text-gray-600 text-sm">
                Our FAQ covers everything from account setup to scoring rules and drafting strategy.
              </p>
            </div>
            <Link
              to="/faq"
              className="flex-shrink-0 bg-purple-600 text-white font-semibold py-2 px-5 rounded-lg hover:bg-purple-700 transition-colors text-sm"
            >
              View FAQ
            </Link>
          </div>

          {/* What to include */}
          <div className="mt-10">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Tips for faster support</h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center text-purple-700 font-bold text-xs mt-0.5">
                  1
                </span>
                <span>
                  <strong>Include your account email</strong> — this helps us look up your account
                  quickly.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center text-purple-700 font-bold text-xs mt-0.5">
                  2
                </span>
                <span>
                  <strong>Describe the issue clearly</strong> — include the league name or
                  tournament name if relevant.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center text-purple-700 font-bold text-xs mt-0.5">
                  3
                </span>
                <span>
                  <strong>Mention your device and browser</strong> — this helps us reproduce
                  technical issues faster.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
